import { INFINITY_TABLE } from "@/config";
import "./News.css";
import {
	DataSource,
	type DataSourceDataParams,
	type DataSourceLivePaginationCursorFn,
	InfiniteTable,
	type InfiniteTablePropColumns,
} from "@infinite-table/infinite-react";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { LookupParams } from "epsm-api-types";
import type { News as NewsType } from "epsm-api-types/models";
import type React from "react";
import { memo, type ReactNode, useCallback, useEffect, useState } from "react";
import { useEventSubscription } from "@/hooks/useEventSubscription";
import { cn } from "@/lib/utils";
import { settings } from "@/lib/utils/settings/settings";
import ErrorHandler from "@/services/ErrorService/ErrorService";
import { DEFAULT_STATE_VALUES } from "@/shared/constants/common";
import { EVENTS } from "@/shared/eventEmiters/consts";
import type { EventEmitter } from "@/shared/eventEmiters/EventEmitter";
import newsConfig from "../config";
import { getColumnHeaderHeight } from "../lib/helpers/getColumnHeaderHeight";
import { isTickerFilter } from "../lib/helpers/isTickerFilter";
import { setNewDataParams } from "../lib/helpers/setNewDataParams";
import { toggleShowDetails } from "../lib/helpers/toggleShowDetails";
import { useNewsFilterByTickerFilter } from "../lib/hooks/useNewsFilterByTickerFilter";
import { useScrollIntoSavedPosition } from "../lib/hooks/useScrollIntoSavedPosition";
import { useSetColumns } from "../lib/hooks/useSetColumns";
import newsApi from "../lib/services/api";
import { UseData } from "../lib/services/UseData";
import type { NewsState } from "../types";
import { NewsDetails } from "./components/NewsDetails";

const {
	initialDataParams,
	defaultSortInfo,
	defaultFilterValue,
	DEFAULT_FILTER_DELAY,
	DETAILS_HEIGHT,
	DEFAULT_COLUMN_ORDER,
	DEFAULT_COLUMN_ORDER_OPTIONS,
} = newsConfig;

type Props = {
	state: NewsState;
	tileId: string;
	eventEmitter: EventEmitter;
	className?: string;
	header?: ReactNode;
};

export const News: React.FC<Props> = memo(
	({ state, eventEmitter, className, header }) => {
		const ticker = state?.ticker ?? DEFAULT_STATE_VALUES.ticker;
		const tickerFilter = state?.tickerFilter;
		const filter = state?.filter;
		const datasetFilters = state?.datasetFilters;
		const categoryFilters = state?.categoryFilters;
		const sectorFilters = state?.sectorFilters;

		const [isWindowExpanded, setIsWindowExpanded] = useState<boolean>(true);
		const [expandedRowDetailList, setExpandedRowDetailList] = useState<
			number[]
		>([]);
		const [lookup, setLookup] = useState<LookupParams>();
		const [rowDetailHeight, setRowDetailHeight] = useState(
			DETAILS_HEIGHT.COLLAPSED,
		);

		const [columnOrder, setColumnOrder] = useState<string[]>(
			state?.columnOrder?.length ? state.columnOrder : [],
		);

		const [columns, setColumns] = useState<InfiniteTablePropColumns<NewsType>>(
			{},
		);

		const [dataParams, setDataParams] =
			useState<Partial<DataSourceDataParams<NewsType>>>(initialDataParams);

		const { execute } = UseData.create().useGetData();

		const options = newsApi.getNewsInfinityQueryOptions(
			ticker,
			DEFAULT_COLUMN_ORDER_OPTIONS,
			state?.pagination
				? state.pagination
				: { limit: newsConfig.DEFAULT_PAGE_SIZE, offset: 0 },
			{
				...dataParams,
				filterValue: state?.filterValue?.length
					? state?.filterValue
					: dataParams.filterValue,
			},
			execute,
			lookup,
			tickerFilter,
			filter,
			datasetFilters,
			categoryFilters,
			sectorFilters,
		);

		const { onReady } = useScrollIntoSavedPosition(ticker);
		useSetColumns(
			setColumnOrder,
			setColumns,
			state?.columnOrder?.length ? state?.columnOrder : DEFAULT_COLUMN_ORDER,
		);

		useNewsFilterByTickerFilter(
			typeof tickerFilter === "string" && isTickerFilter(tickerFilter)
				? tickerFilter
				: ticker,
			setLookup,
		);

		const {
			data,
			fetchNextPage,
			isFetchingNextPage,
			isPlaceholderData,
			isLoading,
			refetch,
		} = useInfiniteQuery(options);

		useEffect(() => {
			const eventSource = new EventSource(
				`${settings.wsHost}/live/news${ticker ? `/${ticker}` : ""}`,
			);
			eventSource.onerror = (error) => {
				ErrorHandler.logError(error);
			};
			eventSource.addEventListener("reload", () => {
				refetch();
			});
			return () => {
				eventSource?.close();
			};
		}, [ticker, refetch]);

		function onDataParamsChange(tableParams: DataSourceDataParams<NewsType>) {
			setNewDataParams(tableParams, setDataParams);
		}

		const livePaginationCursorFn: DataSourceLivePaginationCursorFn<NewsType> =
			useCallback(({ length }) => {
				return length;
			}, []);

		useEventSubscription(
			eventEmitter,
			EVENTS.EXPANDED_WINDOW,
			setIsWindowExpanded,
		);

		if (header && !isLoading && !data?.pages?.length) return null;

		return (
			<div className="h-full">
				{header}
				<div
					id="full-h-el"
					className={cn("h-full", className)}
					data-testid="news-component"
				>
					<DataSource<NewsType>
						primaryKey="title"
						data={data?.pages ?? []}
						onDataParamsChange={onDataParamsChange}
						filterDelay={DEFAULT_FILTER_DELAY}
						sortInfo={defaultSortInfo}
						defaultFilterValue={defaultFilterValue}
						loading={isLoading || isFetchingNextPage || isPlaceholderData}
						livePaginationCursor={livePaginationCursorFn}
					>
						<InfiniteTable<NewsType>
							onScrollToBottom={fetchNextPage}
							onReady={onReady}
							columns={columns}
							columnOrder={columnOrder}
							onColumnOrderChange={setColumnOrder}
							columnDefaultFlex={1}
							rowHeight={INFINITY_TABLE.ROW_HEIGHT}
							columnHeaderHeight={getColumnHeaderHeight(isWindowExpanded)}
							licenseKey={settings.infiniteTableLicenseKey}
							onCellClick={(data) =>
								toggleShowDetails(data, setExpandedRowDetailList)
							}
							rowDetailHeight={rowDetailHeight}
							isRowDetailExpanded={(rowInfo) =>
								expandedRowDetailList.includes(rowInfo.indexInAll)
							}
							rowDetailRenderer={(newsItem) => (
								<NewsDetails
									newsItem={newsItem}
									setRowDetailHeight={setRowDetailHeight}
									setExpandedRowDetailList={setExpandedRowDetailList}
								/>
							)}
							rowClassName="news-row"
						/>
					</DataSource>
				</div>
			</div>
		);
	},
);

News.displayName = "News";
